'use client';

import { DropdownMenu } from '@/shared/components/primitives/dropdown-menu';
import { cn } from '@/shared/utils/cn';
import { useTranslations } from 'next-intl';
import { useGameStore } from '@/features/games/shared/store/game-store';
import { useUser } from '@/features/auth/shared/hooks/use-user';
import { useEffect } from 'react';
import { WishlistData } from '@/features/games/internal/types/wishlists';
import { GameData } from '@/features/games/shared/types/game-data';
import Modal from '@/shared/components/overlay/modal';
import { CreateWishlistOverlay } from '@/features/games/shared/components/wishlists/create-wishlist-overlay';
import { useOverlayStore } from '@/shared/components/overlay/overlay.store';
import { useToastStore } from '@/shared/components/toast/toast-store';
import { ToastContainer } from '@/shared/components/toast/toast';

export const WishlistDropdown = ({ gameData }: { gameData: GameData }) => {
  const t = useTranslations('GamePage');
  const fetchWishlists = useGameStore((s) => s.fetchWishlists);
  const bustWishlistsCache = useGameStore((s) => s.bustWishlistCache);
  const wishListData = useGameStore((s) => s.wishlistData);
  const { loading, authenticatedUser } = useUser();
  const showOverlay = useOverlayStore((state) => state.showOverlay);

  const addToWishList = async (wishlistId: number) => {
    try {
      const response = await fetch(`/api/game/wishlist/${wishlistId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemId: gameData.entityId }),
        cache: 'no-store',
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      useToastStore.getState().showToast(t('wishlistSuccess'), 'success', 5000);
      bustWishlistsCache();
      await fetchWishlists();
    } catch (error) {
      console.error('Error adding to wishlist:', error);
    }
  };

  useEffect(() => {
    if (!loading && authenticatedUser) {
      const fetchData = async () => {
        await fetchWishlists();
      };
      void fetchData();
    }
  }, [loading, authenticatedUser, fetchWishlists]);

  const items =
    (wishListData || []).map((wishlist: WishlistData) => ({
      value: wishlist.id.toString(),
      label: <span className='font-body-bold w-full text-center text-base'>{wishlist.name}</span>,
      onClick: async () => {
        await addToWishList(wishlist.id);
      },
    })) || [];

  items.push({
    value: 'create',
    label: (
      <span className='text-accent font-body-bold w-full text-center text-base'>
        {t('createNewWishlist')}
      </span>
    ),
    onClick: async () => {
      showOverlay('create-wishlist');
    },
  });

  return (
    <>
      <DropdownMenu
        items={items}
        ariaLabel={t('wishlistMenu')}
        align='start'
        menuTitle={t('addToWishlist')}
        variant='button'
        triggerClassName={cn(
          'text-text border-white',
          'min-w-60 justify-center',
          'font-body-bold text-lg',
          'hover:text-text-hover hover:border-[var(--color-text-hover)] hover:bg-transparent',
        )}
        contentClassName={cn('bg-background-contrast lg:bg-background-panel max-w-60')}
        itemClassName={cn(
          'group text-text px-4 py-3 text-base transition-colors',
          'hover:bg-transparent data-[highlighted]:bg-transparent',
          'lg:hover:text-text-hover',
        )}
        withArrow={false}
      />
      <Modal modalKey='create-wishlist'>
        <CreateWishlistOverlay
          onSuccess={(data) => {
            if (data && data.entityId) {
              void addToWishList(data.entityId);
            }
          }}
        />
      </Modal>
      <ToastContainer />
    </>
  );
};
